import type { ReactNode } from 'react';
import { Link } from 'react-router';
import type { ActivityEntry, Resource, Service, User } from '@bekebi/contracts/api';
import { isUuid } from '../../api/ids';
import { Button } from '../../components/button';
import { EmptyState, ErrorState, LoadingState } from '../../components/states';
import { useHasPermission } from '../../session/current-user';
import { useAllResources } from '../infrastructure/api';
import { useServices } from '../services/api';
import { formatDateTime } from '../services/format';
import { useActivity, useUserDirectory } from './api';
import { actionLabel, actorKindLabel, changeLines } from './present';

export interface Lookups {
  users: Map<string, User>;
  services: Map<string, Service>;
  resources: Map<string, Resource>;
}

/**
 * Activity (/app/activity): the portal's audit record, newest first, from GET /api/activity. Each
 * entry shows when it happened (the API's time, in Lagos time), who acted, what they did, what it
 * concerned and what the API recorded about it. Older entries load from the API's own cursor.
 * Nothing is filtered, summarised or inferred in the browser.
 */
export function ActivityOverview() {
  const activity = useActivity();
  const users = useUserDirectory(useHasPermission('users.manage'));
  const services = useServices();
  const resources = useAllResources(useHasPermission('infrastructure.view'));
  const entries = activity.data?.pages.flatMap((p) => p.items) ?? [];
  const lookups: Lookups = {
    users: new Map((users.data ?? []).map((u) => [u.id, u])),
    services: new Map((services.data ?? []).map((s) => [s.id, s])),
    resources: new Map((resources.data ?? []).map((r) => [r.id, r])),
  };

  return (
    <div className="max-w-6xl">
      <h1>Activity</h1>
      <p className="mt-1 text-sm text-muted">
        What was done in the portal, and by whom, newest first. Entries are recorded by the portal
        and cannot be changed or removed.
      </p>

      <div className="mt-6">
        {activity.isPending ? (
          <LoadingState label="Loading activity…" />
        ) : activity.isError && entries.length === 0 ? (
          <ErrorState
            title="Activity could not be loaded"
            message={activity.error.message}
            onRetry={() => void activity.refetch()}
          />
        ) : entries.length === 0 ? (
          <EmptyState
            title="No activity recorded yet."
            description="Actions taken in the portal will appear here."
          />
        ) : (
          <>
            <div
              aria-hidden="true"
              className="hidden grid-cols-[1fr_1.3fr_1.2fr_1.3fr] gap-4 px-4 pb-2 text-xs font-medium uppercase tracking-wide text-muted md:grid"
            >
              <span>Time</span>
              <span>Actor</span>
              <span>Action</span>
              <span>Concerning</span>
            </div>
            <ol aria-label="Activity" className="space-y-2">
              {entries.map((entry) => (
                <li key={entry.id}>
                  <EntryRow entry={entry} lookups={lookups} />
                </li>
              ))}
            </ol>
            <div className="mt-4">
              {activity.isFetchNextPageError ? (
                <p role="alert" className="mb-3 text-sm text-danger">
                  Older activity could not be loaded: {activity.error?.message}
                </p>
              ) : null}
              {activity.hasNextPage ? (
                <Button
                  variant="secondary"
                  onClick={() => void activity.fetchNextPage()}
                  disabled={activity.isFetchingNextPage}
                >
                  {activity.isFetchingNextPage
                    ? 'Loading…'
                    : activity.isFetchNextPageError
                      ? 'Try again'
                      : 'Show older activity'}
                </Button>
              ) : (
                <p className="text-sm text-muted">No older activity.</p>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/** One audit entry. `selfId`: entries by that user show "You" as the actor (My Activity). */
export function EntryRow({
  entry,
  lookups,
  selfId,
}: {
  entry: ActivityEntry;
  lookups: Lookups;
  selfId?: string;
}) {
  const lines = changeLines(entry);
  return (
    <article className="rounded-lg border border-line bg-surface p-4 text-sm">
      <div className="grid gap-2 md:grid-cols-[1fr_1.3fr_1.2fr_1.3fr] md:gap-4">
        <div className="text-fg-2">
          <span className="text-muted md:hidden">Time: </span>
          <time dateTime={entry.occurredAt}>{formatDateTime(entry.occurredAt)}</time>
        </div>
        <div className="min-w-0">
          <span className="text-muted md:hidden">Actor: </span>
          <Actor entry={entry} users={lookups.users} selfId={selfId} />
        </div>
        <div className="min-w-0">
          <h2 className="text-sm font-semibold text-fg">{actionLabel(entry.action)}</h2>
          <p className="break-all font-mono text-xs text-muted">{entry.action}</p>
        </div>
        <div className="min-w-0 break-words">
          <span className="text-muted md:hidden">Concerning: </span>
          <Target entry={entry} lookups={lookups} />
        </div>
      </div>
      {lines.length > 0 ? (
        <details className="mt-3 border-t border-line pt-2">
          <summary className="cursor-pointer text-xs font-medium text-muted">
            Recorded details
          </summary>
          <dl className="mt-2 grid gap-x-4 gap-y-1 text-xs sm:grid-cols-[max-content_1fr]">
            {lines.map((line, index) => (
              <div key={`${line.field}-${index}`} className="contents">
                <dt className="font-medium text-muted">{line.field}</dt>
                <dd className="break-all font-mono text-fg-2">{line.text}</dd>
              </div>
            ))}
          </dl>
        </details>
      ) : null}
    </article>
  );
}

function Actor({
  entry,
  users,
  selfId,
}: {
  entry: ActivityEntry;
  users: Map<string, User>;
  selfId?: string | undefined;
}) {
  const kind = actorKindLabel(entry.actorType);
  const user = entry.actorId ? users.get(entry.actorId) : undefined;
  if (selfId && entry.actorId === selfId) {
    return (
      <span className="block">
        <span className="block text-fg">You</span>
        <span className="block text-xs text-muted">{kind}</span>
      </span>
    );
  }
  if (!user) return <span className="text-fg-2">{kind}</span>;
  return (
    <span className="block">
      <span className="block break-all text-fg">{user.email}</span>
      <span className="block text-xs text-muted">
        {kind} · Bekebi User ID <span className="font-mono">{user.bekebiUserId}</span>
      </span>
    </span>
  );
}

const link = (to: string, text: ReactNode) => (
  <Link
    to={to}
    className="text-accent-link underline decoration-accent-link/40 underline-offset-4 hover:decoration-accent-link"
  >
    {text}
  </Link>
);

/** What the entry concerned, linked to its page where the portal has one. */
export function Target({ entry, lookups }: { entry: ActivityEntry; lookups: Lookups }) {
  const id = entry.entityId && isUuid(entry.entityId) ? entry.entityId : null;
  const service = entry.serviceId ? lookups.services.get(entry.serviceId) : undefined;
  const serviceLink =
    entry.serviceId && isUuid(entry.serviceId)
      ? link(`/app/services/${entry.serviceId}`, service?.name ?? 'Service')
      : null;

  switch (entry.entityType) {
    case 'service':
      return id ? (
        link(`/app/services/${id}`, lookups.services.get(id)?.name ?? 'Service')
      ) : (
        <>Service</>
      );
    case 'infrastructure_resource':
      return id ? (
        link(`/app/infrastructure/${id}`, lookups.resources.get(id)?.name ?? 'Resource')
      ) : (
        <>Resource</>
      );
    case 'service_binding':
      return serviceLink ? <>Binding of {serviceLink}</> : <>Binding</>;
    case 'renewal':
      return (
        <>
          {id ? link(`/app/renewals/${id}`, 'Renewal term') : 'Renewal term'}
          {serviceLink ? <> of {serviceLink}</> : null}
        </>
      );
    case 'monitoring_check':
      return (
        <>
          {id ? link(`/app/monitoring/${id}`, 'Monitoring check') : 'Monitoring check'}
          {serviceLink ? <> on {serviceLink}</> : null}
        </>
      );
    case 'payment_account':
      return link('/app/payment-account', 'Payment Account');
    case 'user': {
      const user = id ? lookups.users.get(id) : undefined;
      return user ? (
        <span className="block">
          <span className="block break-all">{user.email}</span>
          <span className="block text-xs text-muted">
            Bekebi User ID <span className="font-mono">{user.bekebiUserId}</span>
          </span>
        </span>
      ) : (
        <>A portal user</>
      );
    }
    case 'session':
      return <>A sign-in session</>;
    case 'mailbox': {
      // Mailbox entries record the mailbox address as their entity (never a secret).
      const address =
        entry.entityId && /^[^\s@]+@[^\s@]+$/.test(entry.entityId) ? entry.entityId : null;
      const what = address ? (
        <>
          Mailbox <span className="break-all">{address}</span>
        </>
      ) : (
        <>A mailbox</>
      );
      return serviceLink ? (
        <>
          {what} on {serviceLink}
        </>
      ) : (
        what
      );
    }
    case null:
      return serviceLink ?? <span className="text-muted">Nothing specific</span>;
    default:
      return <>{entry.entityType}</>;
  }
}
